import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Button from "@mui/material/Button"
import Close from "@mui/icons-material/Close"
import { Box, Divider, IconButton, MenuItem, Select, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import useUserCanWriteAndDelete from "../hooks/permissions"

type FormDialogProps<T> = {
  open: boolean
  handleClose: () => void
  title: string
  subtitle?: React.ReactNode
  hasCancel?: boolean
  FormComponent: React.ComponentType<any>
  item?: T
  save: (values: T) => Promise<void>
  formComponentProps?: Record<string, any>
  hasDoubleLanguage?: boolean
}

export type FormInDialogProps<T> = {
  item?: T
  save: (values: T) => Promise<void>
  ref?: React.RefObject<HTMLButtonElement | null>
  language?: string
}
export type FormInDialogEditProps<T> = {
  item: T
  formComponentsProps?: Record<string, any>
  save: (values: T) => Promise<void>
  ref: React.RefObject<HTMLButtonElement>
  language?: string
}

export default function DialogForm<T>({
  open,
  handleClose,
  title,
  subtitle,
  hasCancel = false,
  FormComponent,
  formComponentProps,
  item,
  save,
  hasDoubleLanguage = false,
}: FormDialogProps<T>) {
  const { t, i18n } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const [languageForm, setLanguageForm] = useState(i18n.language)

  // change language when user change it
  useEffect(() => {
    setLanguageForm(i18n.language)
  }, [i18n.language])

  const ref = useRef<HTMLButtonElement>(null)

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return
        }
        handleClose()
      }}
      disableEscapeKeyDown
      scroll="paper"
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "semibold" }} component={"h1"}>
            {title}
          </Typography>
          <Box>
            {hasDoubleLanguage && (
              <Select
                value={languageForm}
                onChange={(e) => {
                  setLanguageForm(e.target.value)
                }}
                sx={{ mr: 2, backgroundColor: "white", borderRadius: 1, width: 150 }}
                variant="outlined"
                size="small"
              >
                <MenuItem value="it">
                  <img src="/icons/it.svg" height={14} style={{ marginRight: 10 }} /> Italiano
                </MenuItem>
                <MenuItem value="en">
                  <img src="/icons/en.svg" height={14} style={{ marginRight: 10 }} />
                  English
                </MenuItem>
              </Select>
            )}
            <IconButton aria-label="close" onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        {subtitle}
      </DialogTitle>
      <Divider />

      <DialogContent>
        <FormComponent
          ref={ref}
          item={item}
          save={save}
          title={title}
          {...formComponentProps}
          language={languageForm}
        />
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 4,
        }}
      >
        {hasCancel && (
          <Button size="large" onClick={handleClose} variant="text">
            {t("general.annulla")}
          </Button>
        )}
        {!hasCancel && <Box sx={{ width: "100%" }} />}
        {userCanWriteAndDelete && (
          <Button color="primary" size="large" variant="contained" onClick={() => ref.current?.click()}>
            {t("general.salva")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
